import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { User } from "../../modules/auth/domain/User";
import { UserRepositorySupabase } from "../../modules/auth/infrastructure/UserRepository.supabase";
import { supabase } from "../../modules/shared/infrastructure/supabase";

interface IAuthContext {
  login: (username: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  user?: User;
  loading: boolean;
}

export const AuthContext = createContext<IAuthContext | null>(null);
const userRepository = new UserRepositorySupabase();

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Listen for subsequent auth events (sign-in, sign-out, token refresh).
    // We defer the data query with setTimeout to ensure the session is fully
    // committed to the Supabase client before issuing an RLS-protected query.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        const userId = session.user.id;
        setTimeout(async () => {
          const appUser = await userRepository.findById(userId);
          setUser(appUser ?? undefined);
          setLoading(false);
        }, 0);
      } else {
        setUser(undefined);
        setLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: username,
      password,
    });

    if (error) {
      throw error;
    }

    const appUser = await userRepository.findById(data.user.id);
    if (!appUser) {
      throw new Error("User not found");
    }

    setUser(appUser);
    return appUser;
  }, []);

  const logout = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
  }, []);

  const value = useMemo(
    () => ({ user, login, logout, loading }),
    [login, logout, user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
